import { useEffect, useState } from 'react';
import { Button, InlineError, Input } from '../../components/ui';
import { scoreError, type ScoreApi, type ScoreEntryLink } from './scoreboard';

/** Tokens are shown once in this component only, never cached or persisted. */
export function ScoreEntryLinks({ api, section }: { api: ScoreApi; section: string }) {
  const [links, setLinks] = useState<ScoreEntryLink[]>([]);
  const [url, setUrl] = useState('');
  const [person, setPerson] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let active = true;
    api
      .links(section)
      .then((value) => {
        if (active) setLinks(value);
      })
      .catch((cause) => {
        if (active) setError(scoreError(cause));
      });
    return () => {
      active = false;
    };
  }, [api, section]);
  async function act(action: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await action();
      setLinks(await api.links(section));
    } catch (cause) {
      setError(scoreError(cause));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="score-card score-summary" aria-label="Entry link">
      <h3>Entry link</h3>
      <p>Ads numbers only. Copy a new link now; it cannot be shown again.</p>
      {error ? <InlineError>{error}</InlineError> : null}
      <Input
        label="Who is this link for?"
        hint="Optional. Shown in the Change log, for example PJ."
        maxLength={80}
        value={person}
        onChange={(event) => setPerson(event.target.value)}
      />
      <Button
        disabled={busy}
        variant="secondary"
        onClick={() =>
          void act(async () => {
            const created = await api.createLink(section, person.trim() || undefined);
            setPerson('');
            setUrl(
              `${window.location.origin}/scoreboard-entry/${encodeURIComponent(created.token)}`,
            );
            setNotice('Link created. Copy it before closing.');
          })
        }
      >
        Create entry link
      </Button>
      {url ? (
        <>
          <input aria-label="New entry link" readOnly value={url} />
          <Button
            disabled={busy}
            onClick={() => {
              void navigator.clipboard
                .writeText(url)
                .then(() => setNotice('Link copied.'))
                .catch(() => setError('Copy is unavailable. Select and copy the link above.'));
            }}
          >
            Copy entry link
          </Button>
        </>
      ) : null}
      <ul>
        {links
          .filter((link) => !link.revoked_at)
          .map((link) => (
            <li key={link.id}>
              <strong>
                {link.person_name ? `${link.person_name} (ads entry link)` : 'Ads entry link'}
              </strong>
              {' · '}Created {new Date(link.created_at).toLocaleString()}{' '}
              <Button
                variant="ghost"
                aria-label={`Turn off link ${link.id}`}
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await api.revokeLink(link.id);
                    setUrl('');
                    setNotice('Link turned off.');
                  })
                }
              >
                Turn off<span className="ui-visually-hidden"> link {link.id}</span>
              </Button>
            </li>
          ))}
      </ul>
      <p role="status">{notice}</p>
    </section>
  );
}
